import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 🎫 Wiki - Seção Tickets e Suporte
 * 
 * Documentação completa sobre o sistema de tickets de suporte.
 * Explica como criar tickets, acompanhar status e resolver problemas.
 * Voltada para todos os usuários (não técnica).
 */
const TicketsWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:ticket-confirmation" />
          <span>Tickets e Suporte</span>
        </div>
        <p className={styles.heroText}>
          Sistema de suporte completo para resolver suas dúvidas e problemas. 
          Crie tickets, acompanhe o andamento e receba ajuda da equipe.
        </p>
      </section>

      {/* O que são Tickets */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que são Tickets?</span>
          <p className={styles.sectionSubtitle}>
            Sistema de atendimento e suporte
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              <strong>Tickets</strong> são solicitações de suporte que você cria quando precisa 
              de ajuda, tem uma dúvida ou encontrou um problema. Cada ticket é uma conversa 
              organizada entre você e a equipe de suporte.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              O sistema permite que você <strong>acompanhe o status</strong> de cada ticket, 
              veja as respostas da equipe e adicione novas mensagens conforme necessário.
            </p>
          </div>
        </div>
      </section>

      {/* Como Criar um Ticket */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Criar um Ticket?</span>
          <p className={styles.sectionSubtitle}>
            Passo a passo para solicitar suporte
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:menu" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Acesse a Página de Tickets
                </span>
                <p>
                  No <strong>menu lateral</strong>, acesse <strong>Ajuda → Tickets de Suporte</strong>. 
                  Na página, clique no botão <strong>"Novo Ticket"</strong> ou <strong>"Criar Ticket"</strong>.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:plus-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Clique em "Novo Ticket"
                </span>
                <p>
                  Na página de tickets, clique no botão <strong>"Novo Ticket"</strong> ou 
                  <strong>"Criar Ticket"</strong> para abrir o formulário.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:form-select" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Preencha os Dados
                </span>
                <p>
                  Preencha o <strong>título</strong>, escolha a <strong>categoria</strong> 
                  (Técnico, Financeiro, Administrativo, Geral), defina a <strong>prioridade</strong> 
                  e descreva seu problema ou dúvida.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>4</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:send" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Envie o Ticket
                </span>
                <p>
                  Após preencher todos os campos, clique em <strong>"Criar Ticket"</strong> ou 
                  <strong>"Enviar"</strong>. O ticket será criado e a equipe de suporte será notificada.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>5</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:bell-ring" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Aguarde Resposta
                </span>
                <p>
                  Você receberá uma <strong>notificação</strong> quando a equipe responder seu ticket. 
                  Acompanhe o status na lista de tickets.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Status dos Tickets */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Status dos Tickets</span>
          <p className={styles.sectionSubtitle}>
            Entenda o que cada status significa
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:clock-outline" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Aberto</div>
                <span className={styles.cardTitle}>Open (Aberto)</span>
                <p>
                  Ticket foi criado e está <strong>aguardando resposta</strong> da equipe de suporte.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:reply" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Respondido</div>
                <span className={styles.cardTitle}>In Progress (Respondido)</span>
                <p>
                  A equipe de suporte <strong>respondeu ao ticket</strong> e está aguardando sua resposta.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:account-clock" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Aguardando</div>
                <span className={styles.cardTitle}>Waiting User (Aguardando Resposta)</span>
                <p>
                  A equipe está <strong>aguardando sua resposta</strong> para continuar o atendimento.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:check-circle" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Resolvido</div>
                <span className={styles.cardTitle}>Resolved (Resolvido)</span>
                <p>
                  O problema foi <strong>resolvido</strong> e o ticket está concluído.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Categorias e Prioridades */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Categorias e Prioridades</span>
          <p className={styles.sectionSubtitle}>
            Organize seus tickets corretamente
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:tag" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Categorias</div>
                <span className={styles.cardTitle}>Tipos de Ticket</span>
                <p>
                  <strong>Técnico:</strong> Problemas técnicos, bugs, erros<br/>
                  <strong>Financeiro:</strong> Dúvidas sobre pagamentos, saques, comissões<br/>
                  <strong>Administrativo:</strong> Questões de conta, permissões, configurações<br/>
                  <strong>Geral:</strong> Outras dúvidas ou solicitações
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:alert" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Prioridades</div>
                <span className={styles.cardTitle}>Níveis de Urgência</span>
                <p>
                  <strong>Baixa:</strong> Dúvidas gerais, sem urgência<br/>
                  <strong>Normal:</strong> Questões comuns que precisam de resposta<br/>
                  <strong>Alta:</strong> Problemas que afetam o uso da plataforma<br/>
                  <strong>Urgente:</strong> Problemas críticos que bloqueiam operações
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Dicas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas para um Atendimento Eficiente</span>
          <p className={styles.sectionSubtitle}>
            Como criar tickets que facilitam a resolução
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:file-document-edit" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Seja Específico
                </span>
                <p>
                  Descreva o problema de forma <strong>clara e detalhada</strong>. Inclua passos 
                  para reproduzir o problema, se aplicável.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:tag-multiple" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Escolha a Categoria Correta
                </span>
                <p>
                  Selecione a <strong>categoria e prioridade corretas</strong>. Isso ajuda a 
                  equipe a direcionar seu ticket para o responsável certo.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:reply" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Responda Rapidamente
                </span>
                <p>
                  Quando a equipe responder, <strong>responda o mais rápido possível</strong>. 
                  Isso acelera a resolução do problema.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:check-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Feche Tickets Resolvidos
                </span>
                <p>
                  Quando o problema for resolvido, <strong>feche o ticket</strong>. Isso ajuda 
                  a equipe a organizar melhor o atendimento.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default TicketsWiki;

